import { JetView } from "webix-jet"; // Import JetView class from Webix Jet framework to create application views
import { devConfig } from "config/config"; // Import development configuration utilities

/**
 * Class representing the casino poker report table view.
 * This view renders the treetable and handles data loading and structuring.
 */
export default class tableCasinoReportView extends JetView {

    /**
     * Builds and returns the UI configuration for the report table.
     * @returns {Object} Webix UI configuration object
     */
    config() {

        const _ = this.app.getService("locale")._; // Localization function used for translating UI text
        var vthis = this; // Reference to the current class instance
        const dateFormat = webix.Date.dateToStr(this.app.config.dateFormat); // Formatter for dates based on application configuration

        /**
         * Formats a numeric value as percentage.
         * @param {number} value Numeric value to format
         * @returns {string} Formatted percentage string
         */
        function percentFormat(value) {
            return webix.i18n.numberFormat(value) + "%"; // Convert number to localized format and append percentage symbol
        }

        /**
         * Formats a numeric value as currency.
         * @param {number} value Numeric value to format
         * @returns {string} Currency formatted string
         */
        function penFormat(value) {
            return "$" + webix.i18n.numberFormat(value); // Convert number to localized currency format with dollar symbol
        }

        // Extend Webix summColumn filter to support summing values in a treetable column
        webix.ui.datafilter.summTreeColumn = webix.extend({
            refresh: function (master, node, value) { // Called whenever the column footer needs recalculation
                var result = 0; // Variable that stores accumulated sum
                var resultFormat = 0; // Variable that stores formatted result

                master.data.each(function (obj) { // Iterate through each row of the dataset
                    if (obj.$level == 1) { // Only sum first-level rows (children of root nodes)
                        result += obj[value.columnId]; // Add the value of the current column
                    }
                });

                // Apply formatting depending on the column type
                if (value.columnId === 'CantidadTickets') { // If column represents ticket count
                    resultFormat = webix.i18n.intFormat(result); // Format as integer
                } else {
                    if (value.columnId === 'Profitness') { // If column represents percentage
                        resultFormat = webix.i18n.numberFormat(result) + "%"; // Format as percentage
                    } else {
                        resultFormat = webix.i18n.priceFormat(result); // Otherwise format as currency
                    }
                }

                node.firstChild.innerHTML = resultFormat; // Render formatted result in footer cell
            }

        }, webix.ui.datafilter.summColumn); // Extend base summColumn behavior

        return {
            rows: [ // Main vertical layout container
                {
                    cols: [ // Horizontal layout containing the treetable
                        {
                            view: "treetable", // Webix tree table component
                            id: "datatable2", // Unique identifier for the table
                            footer: false, // Disable table footer
                            css: "webix_header_border webix_data_border", // Custom CSS styling
                            select: true, // Enable row selection
                            tooltip: true, // Enable tooltip display
                            columns: [ // Table column definitions
                                {
                                    id: "label", // Column identifier
                                    header: _("Fecha"), // Column header label
                                    width: 250, // Column width
                                    template: function (obj, common) { // Custom cell renderer
                                        return common.treetable(obj, common) + obj.label; // Render tree icon + label
                                    }
                                },
                                {
                                    id: "Entradas", // Column for money inflows
                                    header: _("Entradas"), // Header label
                                    width: 150, // Column width
                                    format: penFormat // Currency formatting
                                },
                                {
                                    id: "Salidas", // Column for money outflows
                                    header: _("Salidas"), // Header label
                                    width: 150, // Column width
                                    format: penFormat // Currency formatting
                                },
                                {
                                    id: "Rake", // Column for rake value
                                    header: _("Rake"), // Header label
                                    width: 150, // Column width
                                    format: penFormat // Currency formatting
                                },
                                {
                                    id: "GGR", // Column for Gross Gaming Revenue
                                    header: _("Total (GGR)"), // Header label
                                    width: 150, // Column width
                                    format: penFormat // Currency formatting
                                },  
                            ],

                            on: { // Table lifecycle event handlers

                                onBeforeLoad: function () { // Executed before remote data loading
                                    this.showOverlay("<div id='contenedor'; class='container-preload'><div style='position: fixed; top: calc(50% - 57px);left: calc(50% - 60px);width: 120px;' class='progress'><div class='icon-preload girando'></div><div class='load-text'>Loading... </div></div></div>"); // Display loading animation overlay
                                },

                                onAfterLoad: function () { // Executed after remote data loading
                                    this.hideOverlay(); // Hide loading overlay
                                }

                            },

                            ready: function () { // Executed when component initialization completes
                                if (!this.count()) { // Check if table has no records
                                    webix.extend(this, webix.OverlayBox); // Enable overlay functionality
                                    this.showOverlay("<div class='no-data' style='margin:75px; font-size:18px;'>" + _('No hay datos') + "</div>"); // Display "no data" message
                                }
                            }

                        }
                    ]
                }
            ]
        };
    }

    /**
     * Initializes the view and attaches filter event listeners.
     */
    init() {

        const _ = this.app.getService("locale")._; // Localization helper
        const grid = $$("datatable2"); // Reference to treetable component

        const url = devConfig.geturlApi() + "null/Report/GetPokerReport"; // API endpoint for poker report

        let filter = ""; // Variable storing filter query string

        this.on(this.app, "casinoReport:filter", filterData => { // Listen for filter event triggered from filter UI

            let string1 = ""; // Query string builder
            const object = Object.entries(filterData); // Convert filter object to array of key-value pairs

            for (let i = 0; i < object.length; i++) { // Iterate through filter fields

                if (object[i][0] === "Datepicker") { // If filter is date range
                    const date = object[i][1].split(" - "); // Split start and end date
                    string1 += '&dateFrom=' + date[0]; // Add start date parameter
                    string1 += '&dateTo=' + date[1]; // Add end date parameter
                }

                if (typeof object[i][1] !== "object" && object[i][1] !== "" && object[i][0] !== "Datepicker") { // If field is valid and not an object
                    string1 += '&' + object[i][0] + '=' + object[i][1]; // Append filter parameter to query string
                }

            }

            filter = string1; // Save generated filter string

            webix.ajax().get(url + '?start=0&count=10000' + string1) // Perform API request with filters
                .then(res => {

                    const json = res.json(); // Parse response as JSON
                    const raw = json.data || []; // Extract data array or default to empty

                    const structured = this.buildPokerTree(raw); // Transform flat API data into hierarchical tree

                    grid.clearAll(); // Remove existing table data
                    grid.parse(structured); // Populate table with structured tree data

                    if (!structured.length) { // If no data returned
                        grid.showOverlay(_("No hay datos")); // Display "no data" overlay
                    }

                });

            if ($$("filterPopup")) { // If filter popup exists
                $$("filterPopup").hide(); // Close popup after applying filters
            }

        });

    }

    /**
     * Builds hierarchical tree structure for treetable visualization.
     * Groups data by Date -> User -> Provider -> Product -> Transaction.
     * @param {Array} data Raw API data
     * @returns {Array} Structured tree data
     */
    buildPokerTree(data) {

        const days = {}; // Object storing grouped data by day
        const isPlayerReport = data.some(d => d.UserId); // Determine if report includes player-level data

        // Helper function to retrieve or create a node inside a parent
        function getNode(parent, label) {

            let node = parent.data.find(x => x.label === label); // Attempt to find node by label

            if (!node) { // If node does not exist
                node = {
                    id: webix.uid(), // Generate unique node id
                    label: label, // Node label
                    Entradas: 0, // Initialize inflow value
                    Salidas: 0, // Initialize outflow value
                    GGR: 0, // Initialize GGR value
                    Rake: 0, // Initialize rake value
                    data: [] // Child nodes container
                };

                parent.data.push(node); // Add node to parent

            }

            return node; // Return existing or newly created node

        }

        data.forEach(item => { // Iterate through each record returned from API

            const date = (item.Date || "").split(" ")[0] || "Sin fecha"; // Extract date without time

            const entradas = parseFloat(item.MoneyInflows || 0); // Parse money inflows
            const salidas = parseFloat(item.CashOutflows || 0); // Parse money outflows
            const ggr = parseFloat(item.Total || 0); // Parse GGR value
            const rake = parseFloat(item.Rake || 0); // Parse rake value

            if (!days[date]) { // If date group does not exist

                days[date] = {
                    id: "day_" + date, // Unique id for date node
                    label: date, // Display label
                    Entradas: 0, // Initialize inflows
                    Salidas: 0, // Initialize outflows
                    GGR: 0, // Initialize GGR
                    Rake: 0, // Initialize rake
                    data: [] // Children nodes
                };

            }

            const dayNode = days[date]; // Reference to date node

            dayNode.Entradas += entradas; // Accumulate inflows
            dayNode.Salidas += salidas; // Accumulate outflows
            dayNode.GGR += ggr; // Accumulate GGR
            dayNode.Rake += rake; // Accumulate rake

            let currentLevel = dayNode; // Current tree level pointer

            if (isPlayerReport) { // If report includes player grouping

                const userNode = getNode(currentLevel, item.UserId); // Retrieve or create user node

                userNode.Entradas += entradas; // Accumulate inflows
                userNode.Salidas += salidas; // Accumulate outflows
                userNode.GGR += ggr; // Accumulate GGR
                userNode.Rake += rake; // Accumulate rake

                currentLevel = userNode; // Move tree level pointer

            }

            const providerNode = getNode(currentLevel, item.ProviderName || "Sin proveedor"); // Retrieve or create provider node

            providerNode.Entradas += entradas; // Accumulate inflows
            providerNode.Salidas += salidas; // Accumulate outflows
            providerNode.GGR += ggr; // Accumulate GGR
            providerNode.Rake += rake; // Accumulate rake

            currentLevel = providerNode; // Move tree level pointer

            const productNode = getNode(currentLevel, item.ProductName || "Sin producto"); // Retrieve or create product node

            productNode.Entradas += entradas; // Accumulate inflows
            productNode.Salidas += salidas; // Accumulate outflows
            productNode.GGR += ggr; // Accumulate GGR
            productNode.Rake += rake; // Accumulate rake

            productNode.data.push({ // Add transaction-level row
                id: webix.uid(), // Unique id
                label: item.TypeTransaction || "Sin tipo", // Transaction label
                Entradas: entradas, // Transaction inflow
                Salidas: salidas, // Transaction outflow
                GGR: ggr, // Transaction GGR
                Rake: rake // Transaction rake
            });

        });

        return Object.values(days); // Return array of day nodes as root elements

    }

}